/**
 * HottieSheet — Chat and Voice chrome for the one ecosystem assistant.
 *
 * Opened by a long-press on the Orb. Not a full-page dump, not a navigate to
 * hottr.app, not a second Orb, not a HottieDock.
 *
 *   Mobile   bottom sheet (12px on the top two corners)
 *   Desktop  dense column beside the rail, which stays
 *
 * Chat is Screen System CONVERSATION: a transcript, not bubbles. Voice is a
 * slot — Voice Stack owns the session. The dock/rail Orb is the visual; this
 * sheet never draws a second one.
 *
 * Worlds that mount OrbFan or OrbRail get this sheet with no extra wiring.
 * `onHottie` on those components remains an override for hosts that already
 * open Hottie in place.
 *
 * Voice Stack attaches on:
 *   .hottr-hottie-layer[data-open="true"][data-mode="voice"]
 * Chat / Voice tabs are the mode switch. Do not add a second voice button.
 */
import {
  useEffect,
  useId,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import './chassis.css';
import { cn } from './cn';
import { Input } from './Input';
import {
  dispatchHottieSend,
  hottieYouLine,
  type HottieMessage,
  type HottieMode,
} from './hottie';

export interface HottieSheetProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  mode?: HottieMode;
  onModeChange?: (mode: HottieMode) => void;
  /**
   * Transcript lines. Omit to let the sheet own a local transcript (You-lines
   * from the default composer). Pass an array — even empty — to control it.
   * Kit never invents a Hottie reply.
   */
  messages?: readonly HottieMessage[];
  /** Called with the composer text. Optional — send still dispatches. */
  onSend?: (text: string) => void;
  /**
   * Voice Stack slot. Kit does not run a mic, a WebRTC client, or TTS.
   * Rendered in Voice mode only.
   */
  voice?: ReactNode;
  className?: string;
}

const recede = (index: number, length: number): 'newest' | 'previous' | 'oldest' => {
  if (index === length - 1) return 'newest';
  if (index === length - 2) return 'previous';
  return 'oldest';
};

export const HottieSheet = ({
  open,
  onOpenChange,
  mode: modeProp,
  onModeChange,
  messages: messagesProp,
  onSend,
  voice,
  className,
}: HottieSheetProps) => {
  const titleId = useId().replace(/:/g, '');
  const [uncontrolledMode, setUncontrolledMode] = useState<HottieMode>('chat');
  const [localMessages, setLocalMessages] = useState<HottieMessage[]>([]);
  const [draft, setDraft] = useState('');
  const isModeControlled = modeProp !== undefined;
  const isMessagesControlled = messagesProp !== undefined;
  const mode = isModeControlled ? modeProp : uncontrolledMode;
  const messages = isMessagesControlled ? messagesProp : localMessages;

  const setMode = (next: HottieMode) => {
    if (!isModeControlled) setUncontrolledMode(next);
    onModeChange?.(next);
  };

  const dismiss = () => onOpenChange?.(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange?.(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const send = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    onSend?.(text);
    dispatchHottieSend(text);
    if (!isMessagesControlled) {
      setLocalMessages((prev) => [...prev, hottieYouLine(text)]);
    }
    setDraft('');
  };

  const onComposerKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };

  // Voice Stack attaches on .hottr-hottie-layer[data-open="true"][data-mode="voice"].
  return (
    <div
      className={cn('hottr-hottie-layer', className)}
      data-open={open ? 'true' : 'false'}
      data-mode={mode}
    >
      <div
        className="hottr-hottie-scrim"
        data-visible={open ? 'true' : 'false'}
        aria-hidden
        onPointerDown={dismiss}
      />
      <div
        className="hottr-hottie"
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-hidden={open ? undefined : true}
      >
        <div className="hottr-hottie__handle" aria-hidden>
          <span />
        </div>

        <div className="hottr-hottie__bar">
          <h2 id={titleId} className="hottr-hottie__title">
            Hottie
          </h2>
          <div className="hottr-hottie__modes" role="tablist" aria-label="Hottie mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'chat'}
              className="hottr-hottie__mode"
              data-active={mode === 'chat' ? 'true' : 'false'}
              tabIndex={open ? 0 : -1}
              onClick={() => setMode('chat')}
            >
              Chat
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'voice'}
              className="hottr-hottie__mode"
              data-active={mode === 'voice' ? 'true' : 'false'}
              tabIndex={open ? 0 : -1}
              onClick={() => setMode('voice')}
            >
              Voice
            </button>
          </div>
          <button
            type="button"
            className="hottr-hottie__close"
            tabIndex={open ? 0 : -1}
            onClick={dismiss}
          >
            Close
          </button>
        </div>

        {mode === 'chat' ? (
          <div className="hottr-hottie__chat">
            <div className="hottr-hottie__transcript" aria-live="polite">
              {messages.length === 0 ? (
                <p className="hottr-hottie__empty">Waiting when you are.</p>
              ) : (
                messages.map((line, i) => (
                  <article
                    key={line.id}
                    className="hottr-hottie__line"
                    data-recede={recede(i, messages.length)}
                  >
                    <div
                      className="hottr-hottie__speaker"
                      data-you={line.speaker === 'you' ? 'true' : undefined}
                    >
                      {line.speaker === 'you' ? 'You' : 'Hottie'}
                    </div>
                    <p>{line.text}</p>
                  </article>
                ))
              )}
            </div>
            <form className="hottr-hottie__composer" onSubmit={send}>
              <Input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onComposerKey}
                placeholder="Message Hottie"
                aria-label="Message Hottie"
                tabIndex={open && mode === 'chat' ? 0 : -1}
                autoComplete="off"
              />
              <button
                type="submit"
                className="hottr-hottie__send"
                disabled={!draft.trim()}
                tabIndex={open && mode === 'chat' ? 0 : -1}
              >
                Send
              </button>
            </form>
          </div>
        ) : (
          <div className="hottr-hottie__voice">
            <p className="hottr-hottie__empty">
              The Orb is Hottie. No second one.
            </p>
            {voice}
          </div>
        )}
      </div>
    </div>
  );
};
